import { useMemo, useState } from "react";
import { PlusIcon, SlidersHorizontalIcon } from "lucide-react";
import { Icon } from "@/components/atoms/Icon";
import { Popper } from "@/components/atoms/Popper";
import { FilterChipDefinitionList } from "@/components/organisms/UtilityBar/FilterChip/FilterChipDefinitionList";
import { createFilterFromDefinition } from "@/components/organisms/UtilityBar/FilterChip/FilterChip.utils";
import {
  useFilterChipMutations,
  useFilterChipState,
} from "@/components/organisms/UtilityBar/FilterChip/FilterChipStateContext";
import { SEARCH_FILTER_KEY } from "@/graphql/filters";
import { Button } from "@/components/ui/button";

export function FilterChipPopper() {
  const { filters, filterDefinitions } = useFilterChipState();
  const { appendFilter } = useFilterChipMutations();
  const availableFilterDefinitions = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(filterDefinitions).filter(
          ([key]) => !filters.some((filter) => filter.key === key),
        ),
      ),
    [filterDefinitions, filters],
  );
  const [isOpen, setIsOpen] = useState(false);

  if (Object.keys(filterDefinitions).length === 0) {
    return null;
  }

  if (Object.keys(availableFilterDefinitions).length === 0) {
    return null;
  }

  // Chips only. The search term shares ``filters`` but renders as its own
  // input, so counting it here would collapse this into the bare "+" while
  // no chip is on screen to explain what the "+" would be adding to.
  const hasActiveFilters = filters.some(
    (filter) => filter.key !== SEARCH_FILTER_KEY,
  );

  return (
    <Popper
      isHoverable={false}
      isClickable={true}
      isEscapeable={true}
      isOpenState={[isOpen, setIsOpen]}
      anchor={
        <div className="h-full flex flex-row items-center justify-center">
          {/* Dashed, as shadcn's table toolbar draws a filter to add. */}
          {!hasActiveFilters ? (
            <Button
              variant="outline"
              size="sm"
              data-testid="filter-chip-add-filter"
              className="border-dashed text-muted-foreground"
            >
              <Icon ReactIcon={SlidersHorizontalIcon} />
              Filter
            </Button>
          ) : (
            <Button
              variant="outline"
              size="icon-sm"
              aria-label="Add a filter"
              data-testid="filter-chip-add-more"
              className="border-dashed text-muted-foreground"
            >
              <PlusIcon />
            </Button>
          )}
        </div>
      }
    >
      <FilterChipDefinitionList
        filterDefinitions={availableFilterDefinitions}
        onPick={(filterDefinition) => {
          setIsOpen(false);
          setTimeout(() => {
            appendFilter(
              createFilterFromDefinition(
                filterDefinition.key,
                filterDefinition,
              ),
            );
          }, 0);
        }}
        testIdPrefix="filter-definition"
        className="max-h-100 min-w-85 max-w-160"
      />
    </Popper>
  );
}
